/**
 * Invoice Template — Memory Cache + React Hook
 * Pattern: same as usePlatformBranding (singleton cache, one fetch at startup)
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface InvoiceTemplate {
  id: string;
  header_template: string;
  footer_template: string;
}

let _cachedTemplate: InvoiceTemplate | null = null;
let _fetchPromise: Promise<void> | null = null;

/** Sync getter used by print system — zero queries */
export const getCachedInvoiceTemplate = (): InvoiceTemplate | null => _cachedTemplate;

/** Fetch template once at app startup */
export const fetchInvoiceTemplate = async (): Promise<void> => {
  if (_fetchPromise) return _fetchPromise;
  _fetchPromise = (async () => {
    try {
      const { data } = await supabase
        .from('system_invoice_template' as any)
        .select('id, header_template, footer_template')
        .limit(1)
        .maybeSingle();
      if (data) {
        _cachedTemplate = {
          id: (data as any).id || '',
          header_template: (data as any).header_template || '',
          footer_template: (data as any).footer_template || '',
        };
      } else {
        _cachedTemplate = null;
      }
    } catch {
      // Silent fail — fallback to default header/footer
    }
  })();
  return _fetchPromise;
};

/** Force refresh after admin saves template */
export const refreshInvoiceTemplate = async (): Promise<void> => {
  _fetchPromise = null;
  _cachedTemplate = null;
  await fetchInvoiceTemplate();
};

/** React hook for components that need reactivity */
export const useInvoiceTemplate = () => {
  const [template, setTemplate] = useState<InvoiceTemplate | null>(_cachedTemplate);
  const [loading, setLoading] = useState(!_cachedTemplate);

  useEffect(() => {
    const load = async () => {
      if (_cachedTemplate && _cachedTemplate.id) {
        setTemplate(_cachedTemplate);
        setLoading(false);
        return;
      }
      _fetchPromise = null;
      _cachedTemplate = null;
      await fetchInvoiceTemplate();
      setTemplate(_cachedTemplate);
      setLoading(false);
    };
    load();
  }, []);

  return {
    template,
    loading,
    refresh: async () => {
      await refreshInvoiceTemplate();
      setTemplate(_cachedTemplate);
    },
  };
};
